import { jsx } from '@amazon/vinyl-tsx'
import { closeTo, createDisposer, logDebug } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { Icon } from '@/components/Icon'
import { data } from '@amazon/vinyl-observable'
import type { Player } from '@/playback/api'

const { target } = ctx('VolumeControl')

export interface VolumeControlProps {
    readonly deps: {
        readonly player: Player
    }
}

/**
 * Reveals a horizontal volume slider on hover/focus that slides out to the
 * left of the speaker icon. Modeled on
 * VinylGh/packages/vinyl-website/src/components/VolumeControl.tsx.
 */
export function VolumeControl(props: VolumeControlProps) {
    const { player } = props.deps
    const muted = data(player.muted)

    const slider = (
        <input
            className="volumeSlider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            aria-label="Volume"
            value={String(player.volume)}
            style={{
                ['--volume' as never]: `${player.volume * 100}%`,
            }}
            oninput={() => {
                const vol = slider.valueAsNumber
                logDebug(target, 'volume change:', vol)
                player.volume = vol
                player.muted = closeTo(vol, 0)
                slider.style.setProperty('--volume', `${vol * 100}%`)
            }}
        />
    ) as HTMLInputElement

    const view = (
        <div
            className="volumeControl"
            onConnect={() => {
                const { add, dispose } = createDisposer()
                add(
                    player.on('volumeChange', () => {
                        slider.valueAsNumber = player.volume
                        slider.style.setProperty('--volume', `${player.volume * 100}%`)
                    })
                )
                add(
                    player.on('mutedChange', () => {
                        muted.value = player.muted
                    })
                )
                return dispose
            }}
        >
            {slider}
            <button
                className="button volumeBtn"
                title={muted.map((m) => (m ? 'Unmute' : 'Mute'))}
                aria-label={muted.map((m) => (m ? 'Unmute' : 'Mute'))}
                aria-pressed={muted}
                onclick={() => {
                    player.muted = !player.muted
                }}
            >
                <Icon icon="volumeOn" visible={muted.map((m) => !m)} />
                <Icon icon="volumeOff" visible={muted} />
            </button>
        </div>
    )

    return view
}
